import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Spinner, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  BG_1,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  PRIMARY_COLOR,
  RADIUS_LG,
  SHADOW_SM,
} from 'components/upb_viewer_beta/css/betaTokens';
import { bandTier } from 'components/upb_match_explorer/constants';
import { fmtInt, fmtPct } from 'components/upb_match_explorer/format';

const ROW_HEIGHT = 34;

function BandTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) {
    return null;
  }
  const row = payload[0].payload;
  return (
    <div className={css(styles.tooltip)}>
      <div className={css(styles.tooltipName)}>{row.name}</div>
      <div className={css(styles.tooltipRow)}>
        <span>Match rate</span>
        <b>{fmtPct(row.match_pct)}</b>
      </div>
      <div className={css(styles.tooltipRow)}>
        <span>UPB matched</span>
        <b>{fmtInt(row.upb_matched_count)}</b>
      </div>
      <div className={css(styles.tooltipRow)}>
        <span>Active products</span>
        <b>{fmtInt(row.denominator)}</b>
      </div>
    </div>
  );
}

BandTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
};

function MatchBandView({ band, loading, onPartnerClick }) {
  const [mode, setMode] = useState('bars');

  if (loading) {
    return (
      <div className={css(styles.center)}>
        <Spinner animation="border" role="status" size="sm" />
      </div>
    );
  }

  const rows = band?.data || [];
  const tier = bandTier(band?.band ?? 0);

  return (
    <div className={css(styles.panel)}>
      <div className={css(styles.head)}>
        <span className={css(styles.chip)} style={{ backgroundColor: tier.bg, color: tier.text }}>
          <span className={css(styles.dot)} style={{ backgroundColor: tier.color }} />
          {band?.label}
        </span>
        <div className={css(styles.toggle)}>
          <Button
            variant={mode === 'bars' ? 'primary' : 'outline-secondary'}
            size="sm"
            onClick={() => setMode('bars')}
          >
            Bars
          </Button>
          <Button
            variant={mode === 'table' ? 'primary' : 'outline-secondary'}
            size="sm"
            onClick={() => setMode('table')}
          >
            Table
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className={css(styles.empty)}>No distributor partners in this band.</div>
      ) : mode === 'bars' ? (
        <div style={{ width: '100%', height: Math.max(140, rows.length * ROW_HEIGHT + 40) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={rows}
              margin={{ top: 8, right: 48, bottom: 8, left: 8 }}
              barCategoryGap="24%"
            >
              <CartesianGrid stroke={BORDER_2} horizontal={false} />
              <XAxis
                type="number"
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={value => `${value}%`}
                tickLine={false}
                axisLine={{ stroke: BORDER_2 }}
                tick={{ fontSize: 11, fill: FG_3 }}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={170}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: FG_1 }}
              />
              <Tooltip cursor={{ fill: 'rgba(123, 106, 215, 0.08)' }} content={<BandTooltip />} />
              <Bar
                dataKey="match_pct"
                radius={[0, 4, 4, 0]}
                maxBarSize={22}
                cursor="pointer"
                onClick={row => onPartnerClick(row.entity_id)}
              >
                {rows.map(row => (
                  <Cell key={row.entity_id} fill={PRIMARY_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className={css(styles.tableWrap)}>
          <Table hover className={css(styles.table)}>
            <thead>
              <tr>
                <th>Distributor partner</th>
                <th className={css(styles.numCol)}>Match rate</th>
                <th className={css(styles.numCol)}>UPB matched</th>
                <th className={css(styles.numCol)}>Active products</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={row.entity_id}
                  className={css(styles.tableRow)}
                  onClick={() => onPartnerClick(row.entity_id)}
                >
                  <td>{row.name}</td>
                  <td className={css(styles.numCol)}>{fmtPct(row.match_pct)}</td>
                  <td className={css(styles.numCol)}>{fmtInt(row.upb_matched_count)}</td>
                  <td className={css(styles.numCol)}>{fmtInt(row.denominator)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  center: { display: 'flex', justifyContent: 'center', padding: 40, color: PRIMARY_COLOR },
  panel: {
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_SM,
    padding: '20px 22px',
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12.5,
    fontWeight: 600,
    padding: '5px 11px',
    borderRadius: 20,
  },
  dot: { width: 8, height: 8, borderRadius: '50%' },
  toggle: { display: 'inline-flex', gap: 6 },
  empty: { padding: '32px 8px', color: FG_3, fontSize: 14, textAlign: 'center' },
  tableWrap: { overflowX: 'auto' },
  table: { fontSize: 13, color: FG_2, marginBottom: 0 },
  tableRow: { cursor: 'pointer' },
  numCol: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  tooltip: {
    background: FG_1,
    color: BG_1,
    padding: '9px 12px',
    borderRadius: 9,
    fontSize: 12,
    lineHeight: 1.45,
    minWidth: 170,
  },
  tooltipName: { fontWeight: 700, marginBottom: 3 },
  tooltipRow: { display: 'flex', justifyContent: 'space-between', gap: 16, opacity: 0.9 },
});

MatchBandView.propTypes = {
  band: PropTypes.shape({
    band: PropTypes.number,
    label: PropTypes.string,
    total: PropTypes.number,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        entity_id: PropTypes.number,
        name: PropTypes.string,
        match_pct: PropTypes.number,
        upb_matched_count: PropTypes.number,
        denominator: PropTypes.number,
      }),
    ),
  }),
  loading: PropTypes.bool,
  onPartnerClick: PropTypes.func.isRequired,
};

export default MatchBandView;
